import { ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { PAGE_SIZE_OPTIONS } from '@app/custom/features/rrs-product-listing/configs/rrs-view.config';
import {
  RrsPDPFacets,
  RrsWidthFacetCodes,
} from '@app/custom/features/rrs-product-listing/model';
import {
  ProductGridPlaceholder,
  RrsProductSearchPage,
} from '@app/custom/features/rrs-product-listing/model/rrs-product-listing.model';
import { RrsProductFacetService } from '@app/custom/features/rrs-product-listing/services/rrs-product-facet-service.service';
import { RrsProductListComponentService } from '@app/custom/features/rrs-product-listing/services/rrs-product-list/rrs-product-list-component.service';
import { RrsProductSearchService } from '@app/custom/features/rrs-product-listing/services/rrs-product-search/rrs-product-search.service';
import { RrsEventsDispatcherService } from '@app/custom/features/rrs-tms/rrs-adobe-experience/events/services/rrs-events.dispatcher';
import { AutoUnsubscribe } from '@app/shared/decorators';
import {
  CmsService,
  ContentSlotData,
  GlobalMessageService,
  isNotNullable,
  PageMetaService,
  WindowRef,
} from '@spartacus/core';
import { ContentSlotComponentData } from '@spartacus/core/src/cms/model/content-slot-component-data.model';
import {
  BREAKPOINT,
  BreakpointService,
  PageLayoutService,
  ProductListComponent,
  ProductListComponentService,
  ViewConfig,
  ViewModes,
} from '@spartacus/storefront';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { combineLatest, Observable, Subscription } from 'rxjs';
import {
  filter,
  distinctUntilChanged,
  map,
  switchMap,
  take,
} from 'rxjs/operators';

@AutoUnsubscribe()
@Component({
  selector: 'rrs-product-listing',
  templateUrl: './rrs-product-listing.component.html',
  styleUrls: ['./rrs-product-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
})
export class RrsProductListingComponent
  extends ProductListComponent
  implements OnInit, OnDestroy
{
  isDesktop$ = this.breakpointService.isUp(BREAKPOINT.lg);
  viewType$: Observable<ViewModes> =
    this.rrsProductListComponentService.viewMode$;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  viewModes = ViewModes;
  subscriptions = new Subscription();
  response?: Response;
  seoHeading$ = this.pageMetaService.getMeta().pipe(
    filter(isNotNullable),
    map((meta) => (meta.heading || meta.title) ?? '')
  );

  queryParams$ = this.productFacetService.result$.pipe(
    map((data: RrsProductSearchPage) => {
      const getWidth = (width: string): string => {
        const facetIndex = Object.keys(RrsWidthFacetCodes).indexOf(width);
        const facetValue = Object.values(RrsWidthFacetCodes)[facetIndex];

        return facetValue;
      };

      const {
        size,
        sizeBaby,
        sizeBigKid,
        sizeLittleKid,
        sizeMens,
        sizeToddler,
        sizeWomens,
        width,
      } = data.selectedFacets || {};
      const queryParams: RrsPDPFacets = {};

      size ||
      sizeBaby ||
      sizeBigKid ||
      sizeLittleKid ||
      sizeMens ||
      sizeToddler ||
      sizeWomens
        ? (queryParams.sizes =
            size?.[0] ||
            sizeBaby?.[0] ||
            sizeBigKid?.[0] ||
            sizeLittleKid?.[0] ||
            sizeMens?.[0] ||
            sizeToddler?.[0] ||
            sizeWomens?.[0])
        : null;
      width ? (queryParams.width = getWidth(width?.[0])) : null;

      return queryParams;
    })
  );
  protected gridPlaceholderSettings$: Observable<
    Map<number, ProductGridPlaceholder>
  > = this.cmsService.getContentSlot('product_grid_content').pipe(
    switchMap((slotData: ContentSlotData) => {
      return combineLatest(
        slotData.components
          ?.filter(
            (component) => component.flexType === 'product_grid_placeholder'
          )
          ?.map((component: ContentSlotComponentData) => {
            return this.cmsService.getComponentData<ProductGridPlaceholder>(
              component?.uid!
            );
          })!
      );
    }),
    map((productPlaceholders) => {
      let gridSettings = new Map<number, ProductGridPlaceholder>();
      productPlaceholders.forEach((productPlaceholder, index) => {
        gridSettings.set(
          calculatePlaceholderGridPosition(productPlaceholder.grid_position),
          {
            ...productPlaceholder,
            index: index + 1,
          }
        );
      });
      return gridSettings;
    })
  );

  constructor(
    pageLayoutService: PageLayoutService,
    scrollConfig: ViewConfig,
    globalMessageService: GlobalMessageService,
    protected rrsProductListComponentService: RrsProductListComponentService,
    protected breakpointService: BreakpointService,
    protected productFacetService: RrsProductFacetService,
    protected cmsService: CmsService,
    protected eventsDispatcher: RrsEventsDispatcherService,
    protected viewport: ViewportScroller,
    protected productSearchService: RrsProductSearchService,
    protected winRef: WindowRef,
    protected pageMetaService: PageMetaService
  ) {
    super(
      pageLayoutService,
      rrsProductListComponentService as unknown as ProductListComponentService,
      globalMessageService,
      scrollConfig
    );
    if (!this.winRef.isBrowser()) {
      this.response = inject(RESPONSE);
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subscriptions.add(
      this.model$.pipe(take(1)).subscribe((productSearchPage) => {
        if (productSearchPage.pagination?.totalResults == 0) {
          this.response?.status(404);
        }
      })
    );
    this.subscriptions.add(
      this.productFacetService.result$
        .pipe(
          map((res) => res.breadcrumbs),
          distinctUntilChanged((prev, current) => {
            return JSON.stringify(prev) === JSON.stringify(current);
          })
        )
        .subscribe((activeFacets) => {
          const facets: string[] = [];
          activeFacets?.forEach((facet) => {
            facets.push(`${facet.facetName}:${facet.facetValueName}`);
          });
          this.eventsDispatcher.dispatchFacetViewedEvent(facets);
        })
    );
  }

  ngOnDestroy(): void {
    this.productSearchService.clearResults();
    super.ngOnDestroy();
  }

  override sortList(sortCode: string): void {
    this.eventsDispatcher.dispatchSortEvent(sortCode);
    super.sortList(sortCode);
  }

  onPaginationChange(): void {
    this.viewport.scrollToPosition([0, 0]);
  }

  protected readonly undefined = undefined;
}

/**
 * Function to calculate the PLP item index after which the placeholder will be rendered.
 * From Contentstack we get 1,2,3 or 4 which is mapped to positions 5,11,17 and 23.
 * @param position
 */
function calculatePlaceholderGridPosition(position: string): number {
  const positionIndex = parseInt(position, 10) - 1;
  const placeholderPositionsMap = [5, 11, 17, 23];
  return placeholderPositionsMap[positionIndex] - 2;
}
